<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import type { AuthUser } from '../../core/state/auth-store';
import type { HeroProfile } from '../game/hero-api';
import {
  classLabel, HERO_CLASSES, type HeroClass,
  fetchRerollQuote, retireHero, type RerollQuote,
} from '../game/hero-api';
import { confirmAction } from '../game/confirm';
import {
  fetchAccount, changePassword, resendVerification, submitKyc, setCountry,
  setAccountLocale, setAccountCurrency, updateProfile, type AccountInfo,
} from '../game/account-api';
import {
  fetchTopupHistory, fetchInvoices, downloadInvoicePdf, fetchLedger,
  type PurchaseEntry, type InvoiceEntry, type LedgerEntry,
} from '../game/topup-api';
import { fetchReferralInfo, type ReferralInfo } from '../game/referral-api';
import { fetchLegalPages, legalDocTitle, sortLegalDocs, type LegalPageMeta } from '../game/legal-api';
import LegalPanel from './LegalPanel.vue';
import * as music from '../game/music';
import * as sfx from '../game/sfx';
import { classArt } from '../game/art';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import { toast } from '../game/toast';
import {
  t, setLocale, activeLocale, LOCALES, CURRENCIES, isLocaleId, isCurrencyCode,
  type LocaleId, type CurrencyCode,
} from '../i18n';
// Only the locale flag is needed here: the currency row keys off
// `offeredCurrencies`, which already collapses to one entry on a
// single-currency build AND drops currencies with no known rate.
import { hasLocaleChoice } from '../i18n/locales';
import {
  formatMoney, formatNumber, formatDate, displayCurrency, setDisplayCurrency,
  applyAccountCurrency, isConverted, hasRate, CHARGE_CURRENCY,
} from '../i18n/money';

const props = defineProps<{ user: AuthUser; hero: HeroProfile | null }>();
const emit = defineEmits<{ (e: 'goto-reserve'): void }>();

type Tab = 'account' | 'papers' | 'referral' | 'purchases' | 'invoices' | 'transactions' | 'danger';
const tab = ref<Tab>('account');

const account = ref<AccountInfo | null>(null);
const loading = ref(true);
const error = ref('');

// Was pinned to 'en-US' — now follows the player's tongue like every other date.
const memberSince = computed(() => account.value
  ? formatDate(account.value.createdAt, { month: 'long', year: 'numeric' })
  : '');

async function loadAccount() {
  try {
    account.value = await fetchAccount();
    userCountry.value = account.value.country ?? '';
    profileFirstName.value = account.value.firstName ?? '';
    profileLastName.value = account.value.lastName ?? '';
    profilePhone.value = account.value.phone ?? '';
    // The account's stored preferences win over the guest/browser guess: they
    // are what this player deliberately chose, and they should follow them
    // between devices. `main.ts` has already applied the guess by now.
    if (isLocaleId(account.value.locale)) {
      pickedLocale.value = account.value.locale;
      if (account.value.locale !== activeLocale()) await setLocale(account.value.locale);
    }
    applyAccountCurrency(account.value.currency);
    pickedCurrency.value = displayCurrency();
  }
  catch (e) { error.value = (e as Error).message; }
  finally { loading.value = false; }
}
onMounted(loadAccount);

// ── Tongue & coin (the locale + currency switcher) ──
// Applied on change rather than behind a Save button: a language switch that
// needs confirming reads as broken, and the preference is trivially reversible.
const pickedLocale = ref<LocaleId>(activeLocale());
const pickedCurrency = ref<CurrencyCode>(displayCurrency());

/** Only currencies the server gave us a rate for can honestly be displayed. */
const offeredCurrencies = computed(() =>
  CURRENCIES.filter((c) => c.code === CHARGE_CURRENCY || hasRate(c.code)));

async function onPickLocale(id: LocaleId) {
  if (!isLocaleId(id) || id === activeLocale()) return;
  await setLocale(id);
  pickedLocale.value = id;
  const label = LOCALES.find((l) => l.id === id)?.endonym ?? id;
  toast(t('settings.prefs.localeSaved', { language: label }));
  // Persist after the UI has already turned — the switch must not feel gated on
  // a round trip, and a failed save only costs the cross-device carry.
  try {
    await setAccountLocale(id);
    if (account.value) account.value.locale = id;
  } catch { /* stays local; localStorage already remembers it */ }
}

// ── Sound (QA 86cb1qvpt: "Add music, music toggle") ──
// Same apply-on-change contract as the locale switcher: flipping sound and
// then confirming it would read as broken. Both prefs persist in localStorage
// inside their modules, so they hold across reloads and screens.
const musicOn = ref(music.isEnabled());
const sfxOn = ref(!sfx.isMuted());
function onToggleMusic() {
  musicOn.value = !musicOn.value;
  music.setEnabled(musicOn.value);
}
function onToggleSfx() {
  sfxOn.value = !sfxOn.value;
  sfx.setMuted(!sfxOn.value);
}

async function onPickCurrency(code: CurrencyCode) {
  if (!isCurrencyCode(code) || code === displayCurrency()) return;
  setDisplayCurrency(code);
  pickedCurrency.value = code;
  toast(t('settings.prefs.currencySaved', { currency: code }));
  try {
    await setAccountCurrency(code);
    if (account.value) account.value.currency = code;
  } catch { /* stays local */ }
}

// ── Profile fields (name, surname, phone) ──
// One shared Save button for all three — they're one form, not three
// independent settings like locale/currency (which apply on pick).
const profileFirstName = ref('');
const profileLastName = ref('');
const profilePhone = ref('');
const profileSaving = ref(false);
const profileDirty = computed(() =>
  !!account.value && (
    profileFirstName.value !== (account.value.firstName ?? '')
    || profileLastName.value !== (account.value.lastName ?? '')
    || profilePhone.value !== (account.value.phone ?? '')
  ));
async function onSaveProfile() {
  if (!profileDirty.value) return;
  profileSaving.value = true;
  try {
    // Send ONLY what the player actually changed. Submitting all three meant a
    // name-only edit also re-submitted the stored phone — and if that value
    // failed the phone format (older/shorter numbers do), saving a surname died
    // on "Invalid phone number", a field the player never touched
    // (QA 2026-08-17). Untouched fields are simply absent from the patch.
    const patch: Parameters<typeof updateProfile>[0] = {};
    if (profileFirstName.value !== (account.value?.firstName ?? '')) patch.firstName = profileFirstName.value;
    if (profileLastName.value !== (account.value?.lastName ?? '')) patch.lastName = profileLastName.value;
    if (profilePhone.value !== (account.value?.phone ?? '')) patch.phone = profilePhone.value;
    const res = await updateProfile(patch);
    if (account.value) {
      account.value.firstName = res.firstName;
      account.value.lastName = res.lastName;
      account.value.phone = res.phone;
    }
    profileFirstName.value = res.firstName ?? '';
    profileLastName.value = res.lastName ?? '';
    profilePhone.value = res.phone ?? '';
    toast(t('settings.profile.detailsSaved'));
  } catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { profileSaving.value = false; }
}

// ── Invoice PDF — fetched through the app (never a bare link), so an expired
//    access cookie is refreshed and retried instead of showing the player a raw
//    401 JSON page (QA 2026-08-17). See downloadInvoicePdf.
const invoiceBusy = ref<string | null>(null);
async function onDownloadInvoice(invoiceNumber: string) {
  invoiceBusy.value = invoiceNumber;
  try { await downloadInvoicePdf(invoiceNumber); }
  catch { toast(t('settings.invoices.downloadFailed'), { kind: 'error' }); }
  finally { invoiceBusy.value = null; }
}

// ── Billing country (dominion: Settings → Profile → Country, drives VAT) ──
const userCountry = ref('');
const countrySaving = ref(false);
async function onSaveCountry() {
  if (userCountry.value.length !== 2) return;
  countrySaving.value = true;
  try {
    await setCountry(userCountry.value);
    if (account.value) account.value.country = userCountry.value;
    toast(t('settings.profile.country.saved'));
  } catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { countrySaving.value = false; }
}

// ── Email verification resend ──
const verifySending = ref(false);
const verifySent = ref(false);
async function onResend() {
  verifySending.value = true;
  try { await resendVerification(); verifySent.value = true; }
  catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { verifySending.value = false; }
}

// ── Change password ──
const pwCurrent = ref('');
const pwNew = ref('');
const pwConfirm = ref('');
const pwSaving = ref(false);
async function onSavePassword() {
  if (pwNew.value.length < 8) { toast(t('settings.password.tooShort'), { kind: 'error' }); return; }
  if (pwNew.value !== pwConfirm.value) { toast(t('settings.password.mismatch'), { kind: 'error' }); return; }
  pwSaving.value = true;
  try {
    await changePassword(pwCurrent.value, pwNew.value);
    pwCurrent.value = ''; pwNew.value = ''; pwConfirm.value = '';
    toast(t('settings.password.changed'));
  } catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { pwSaving.value = false; }
}

// ── KYC (Papers) ──
const kycName = ref('');
const kycDob = ref('');
const kycAddress = ref('');
const kycCountry = ref('');
const kycFile = ref<File | null>(null);
const kycSubmitting = ref(false);
const COUNTRIES: Array<[string, string]> = [
  ['AT', 'Austria'], ['BE', 'Belgium'], ['BG', 'Bulgaria'], ['HR', 'Croatia'],
  ['CY', 'Cyprus'], ['CZ', 'Czech Republic'], ['DK', 'Denmark'], ['EE', 'Estonia'],
  ['FI', 'Finland'], ['FR', 'France'], ['DE', 'Germany'], ['GR', 'Greece'],
  ['HU', 'Hungary'], ['IE', 'Ireland'], ['IT', 'Italy'], ['LV', 'Latvia'],
  ['LT', 'Lithuania'], ['LU', 'Luxembourg'], ['MT', 'Malta'], ['NL', 'Netherlands'],
  ['PL', 'Poland'], ['PT', 'Portugal'], ['RO', 'Romania'], ['SK', 'Slovakia'],
  ['SI', 'Slovenia'], ['ES', 'Spain'], ['SE', 'Sweden'],
  ['GB', 'United Kingdom'], ['US', 'United States'],
];
function onKycFile(e: Event) {
  kycFile.value = (e.target as HTMLInputElement).files?.[0] ?? null;
}
const kycFormValid = computed(() =>
  kycName.value.trim().length > 1 && !!kycDob.value
  && kycAddress.value.trim().length > 3 && kycCountry.value.length === 2 && !!kycFile.value);
async function onSubmitKyc() {
  if (!kycFormValid.value || !kycFile.value) return;
  kycSubmitting.value = true;
  try {
    await submitKyc({
      fullName: kycName.value.trim(),
      dateOfBirth: kycDob.value,
      address: kycAddress.value.trim(),
      country: kycCountry.value,
      document: kycFile.value,
    });
    toast(t('settings.papers.submitted'));
    await loadAccount();
  } catch (e) { toast((e as Error).message, { kind: 'error' }); }
  finally { kycSubmitting.value = false; }
}

// ── The law shelf (Settings → Papers → published legal documents) ──
// The directory is whatever admins keep active in the CMS — the same set the
// web footer serves — so a new or retired policy shows up here on its own.
const legalDocs = ref<LegalPageMeta[]>([]);
const legalDocsLoaded = ref(false);
/** Slug of the doc open in the reader, or null when the reader is closed. */
const legalDocOpen = ref<string | null>(null);

// ── Referral (Settings → Referral) ──
const referral = ref<ReferralInfo | null>(null);
const referralLoaded = ref(false);
// Local error, not the shared `error` ref above — a failed fetch here (e.g. the
// endpoint not deployed yet) should only blank this section, not the whole page.
const referralError = ref('');
const referralLink = computed(() => referral.value ? `${location.origin}/?ref=${referral.value.code}` : '');
const referralCopied = ref(false);
async function copyReferralLink() {
  if (!referralLink.value) return;
  try {
    await navigator.clipboard.writeText(referralLink.value);
    referralCopied.value = true;
    window.setTimeout(() => { referralCopied.value = false; }, 1800);
  } catch { /* clipboard permission denied — nothing to recover, just skip the flash */ }
}

// ── Billing records (moved out of The Hoard — each its own page) ──
const purchases = ref<PurchaseEntry[]>([]);
const invoices = ref<InvoiceEntry[]>([]);
const ledger = ref<LedgerEntry[]>([]);
const purchasesLoaded = ref(false);
const invoicesLoaded = ref(false);
const ledgerLoaded = ref(false);
async function openTab(next: Tab) {
  tab.value = next;
  if (next === 'papers' && !legalDocsLoaded.value) {
    // Own catch: a failed directory fetch just leaves the shelf empty — it
    // must not take the KYC form (or the whole book) down with it.
    try { legalDocs.value = sortLegalDocs(await fetchLegalPages()); }
    catch { /* shelf stays empty */ }
    finally { legalDocsLoaded.value = true; }
    return;
  }
  // Refetch EVERY time this tab is opened. It used to be cached behind
  // `!referralLoaded`, which is never reset, so the recruited count froze at
  // whatever it was the first time the tab was viewed in that session — a
  // friend signing up changed nothing until a full page reload.
  // (The parameter was also named `t`, shadowing the i18n `t`, so the catch
  // below called a string and threw instead of showing the error. Plain tsc
  // does not typecheck SFC bodies, so it never surfaced.)
  if (next === 'referral') {
    referralError.value = '';
    try { referral.value = await fetchReferralInfo(); }
    catch (e) { referralError.value = (e as Error).message || t('settings.referral.loadFailed'); }
    finally { referralLoaded.value = true; }
    return;
  }
  try {
    if (next === 'purchases' && !purchasesLoaded.value) {
      purchases.value = await fetchTopupHistory();
      purchasesLoaded.value = true;
    } else if (next === 'invoices' && !invoicesLoaded.value) {
      invoices.value = await fetchInvoices();
      invoicesLoaded.value = true;
    } else if (next === 'transactions' && !ledgerLoaded.value) {
      ledger.value = await fetchLedger({ limit: 100 });
      ledgerLoaded.value = true;
    } else if (next === 'danger' && !rerollQuoteLoaded.value) {
      rerollQuote.value = await fetchRerollQuote();
      rerollQuoteLoaded.value = true;
    }
  } catch (e) { error.value = (e as Error).message; }
}

// ── Danger zone (Settings → delete/reroll the hero, dominion parity: 20%
//    of the wiped inventory's value comes back as compensation) ──
const rerollQuote = ref<RerollQuote | null>(null);
const rerollQuoteLoaded = ref(false);
const rerollClass = ref<HeroClass>(props.hero?.hero.class ?? 'WARRIOR');
const rerollName = ref('');
const dangerBusy = ref(false);

function compensationRows() {
  const rows = [
    { label: t('settings.danger.inventoryValue'), value: formatNumber(rerollQuote.value?.inventoryValue ?? 0), icon: cashIcon, tone: 'dim' as const },
    { label: t('settings.danger.compensation'), value: formatNumber(rerollQuote.value?.compensation ?? 0), icon: cashIcon, tone: 'success' as const },
  ];
  // Rune-bought gear is compensated in runes — shown only when the player owns
  // some, so the common silver-only case reads exactly as before.
  if ((rerollQuote.value?.inventoryValuePremium ?? 0) > 0) {
    rows.push(
      { label: t('settings.danger.inventoryValue'), value: formatNumber(rerollQuote.value?.inventoryValuePremium ?? 0), icon: runesIcon, tone: 'dim' as const },
      { label: t('settings.danger.compensation'), value: formatNumber(rerollQuote.value?.compensationPremium ?? 0), icon: runesIcon, tone: 'success' as const },
    );
  }
  return rows;
}

async function onDeleteHero() {
  const ok = await confirmAction({
    kicker: t('settings.danger.kicker'),
    title: t('settings.danger.deleteTitle'),
    rows: compensationRows(),
    note: t('settings.danger.deleteNote'),
    confirmLabel: t('settings.danger.deleteConfirm'),
    danger: true,
  });
  if (!ok) return;
  dangerBusy.value = true;
  try {
    await retireHero('delete');
    // The shell caches hero state everywhere (header, nav, tabs) — a full
    // reload is the honest reset, same contract as every other hard state
    // change in this file's neighbourhood (locale, currency are soft; this
    // one genuinely tears down the "you have a hero" world).
    window.location.reload();
  } catch (e) {
    toast((e as Error).message, { kind: 'error' });
    dangerBusy.value = false;
  }
}

async function onRerollHero() {
  const cls = rerollClass.value;
  const ok = await confirmAction({
    kicker: t('settings.danger.kicker'),
    title: t('settings.danger.rerollTitle', { class: classLabel(cls) }),
    icon: classArt(cls),
    rows: compensationRows(),
    note: t('settings.danger.rerollNote'),
    confirmLabel: t('settings.danger.rerollConfirm'),
    danger: true,
  });
  if (!ok) return;
  dangerBusy.value = true;
  try {
    await retireHero('reroll', cls, rerollName.value.trim() || undefined);
    window.location.reload();
  } catch (e) {
    toast((e as Error).message, { kind: 'error' });
    dangerBusy.value = false;
  }
}

/**
 * Billing figures are shown `exact` — in the currency the record itself carries
 * (the one the card was actually charged in), never in today's display pick. A
 * purchase or an invoice that already happened has one true amount; converting
 * it at the current rate would print a number that matches neither the card
 * statement nor the invoice PDF, and would change every time the player
 * switched currency in settings. The display currency belongs on price tags,
 * not on records. An unrecognised code falls back to the base currency rather
 * than being handed to Intl unchecked.
 */
const price = (cents: number, currency?: string) =>
  formatMoney(cents, { exact: true, currency: isCurrencyCode(currency) ? currency : undefined });
const dateOf = (iso: string) => formatDate(iso, { year: 'numeric', month: 'short', day: 'numeric' });
const timeOf = (iso: string) => formatDate(iso, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});

const kycLabel = computed(() => {
  switch (account.value?.kycStatus) {
    case 'APPROVED': return t('settings.kyc.approved');
    case 'PENDING': return t('settings.kyc.pending');
    case 'REJECTED': return t('settings.kyc.rejected');
    default: return t('settings.kyc.none');
  }
});

/** Server enum → localised label, falling back to the raw value if unmapped. */
function purchaseStatus(status: string): string {
  const key = `settings.purchases.status.${status}`;
  const label = t(key);
  return label === key ? status : label;
}

/** Invoice status label; PENDING reads "Not paid" (a documented unpaid/declined charge, not hidden). */
function invoiceStatus(status: string): string {
  const key = `settings.invoices.status.${status}`;
  const label = t(key);
  return label === key ? status : label;
}
</script>

<template>
  <section>
    <!-- header — the venue title grammar (gilt display + rune divider), no eyebrow -->
    <h1 class="ob-h1 font-heading font-black text-[28px] gilt-text tracking-[0.03em] leading-tight">{{ t('settings.title') }}</h1>

    <p v-if="loading" class="text-text-dim">{{ t('common.loading') }}</p>
    <p v-else-if="error" class="text-sm text-error">{{ error }}</p>

    <!-- ══ THE SPREAD — one open book, not a dashboard. ══════════════════════
         The old page was dominion's Settings 1:1 (sidebar of rows + a stack of
         framed cards) — the exact scaffold every project ships. Here the book
         itself is the structure: the LEFT page is your oath (who you are —
         portrait, standing, what the Althing holds against your name), always
         open; the RIGHT page is the working ledger, turned by notches cut into
         its head (same beam as The Hoard). One frame, two pages, a stitched
         spine between them. -->
    <div v-else-if="account" class="ob-spread surf-plank bound-frame">

      <!-- ── the oath page (identity — always in view) ── -->
      <aside class="ob-oath">
        <div class="ob-portrait">
          <img v-if="props.hero" :src="classArt(props.hero.hero.class)" :alt="props.hero.hero.class" />
          <span v-else class="ob-portrait-rune font-heading">{{ (account.username ?? '?').slice(0, 1).toUpperCase() }}</span>
        </div>
        <div class="ob-id">
          <div class="ob-name">{{ (account.username ?? t('settings.oath.anonymous')).toUpperCase() }}</div>
          <div class="font-body text-sm text-text-dim break-all">{{ account.email }}</div>
          <div v-if="memberSince" class="font-body text-xs text-text-dim italic">{{ t('settings.oath.swornIn', { date: memberSince }) }}</div>
          <div class="ob-badges flex flex-wrap justify-center gap-2 mt-1">
            <span v-if="props.hero" class="st-badge">
              {{ t('settings.oath.classLevel', { class: classLabel(props.hero.hero.class), level: props.hero.progression.level }) }}
            </span>
            <span v-if="props.hero" class="st-badge st-badge--gold">
              <img :src="runesIcon" class="w-3.5 h-3.5 object-contain" alt="" />
              {{ t('settings.oath.runes', { count: formatNumber(props.hero.progression.goldBars) }) }}
            </span>
          </div>
        </div>

        <!-- standing marks: what the book holds against your name -->
        <div class="ob-marks">
          <div v-if="!account.emailVerified" class="st-plate st-plate--amber">
            <span>{{ verifySent ? t('settings.marks.verifySent') : t('settings.marks.emailUnverified') }}</span>
            <button v-if="!verifySent" class="btn-forge btn-forge--rune btn-forge--sm" :disabled="verifySending" @click="onResend">
              {{ verifySending ? t('common.working') : t('common.verify') }}
            </button>
          </div>
          <div v-else class="st-plate st-plate--green">{{ t('settings.marks.emailVerified') }}</div>

          <div
            v-if="account.kycEnabled"
            class="st-plate"
            :class="{
              'st-plate--green': account.kycStatus === 'APPROVED',
              'st-plate--amber': account.kycStatus === 'PENDING',
              'st-plate--red': account.kycStatus === 'REJECTED',
              'st-plate--dim': account.kycStatus === 'NONE',
            }"
            :style="account.kycStatus !== 'APPROVED' ? 'cursor:pointer' : ''"
            @click="account.kycStatus !== 'APPROVED' && openTab('papers')"
          >
            <span>{{ kycLabel }}</span>
            <span v-if="account.kycStatus !== 'APPROVED'" class="ml-auto text-text-dim text-xs font-body italic">{{ t('settings.marks.papersLink') }}</span>
          </div>
        </div>

        <!-- The admin panel is the SPA route /admin-panel (Caddy redirects /admin there). -->
        <a
          v-if="props.user.role === 'ADMIN'"
          class="btn-forge btn-forge--carved btn-forge--sm ob-admin"
          href="/admin-panel" target="_blank" rel="noopener"
        >{{ t('settings.oath.adminPanel') }}</a>
      </aside>

      <!-- the stitched spine between the two pages -->
      <div class="ob-spine" aria-hidden="true"></div>

      <!-- ── the ledger page (the working sections, turned by head-notches) ── -->
      <div class="ob-ledger">
        <nav class="flex flex-wrap items-center gap-1 border-b border-border/60 mb-5">
          <button :class="['notch-tab shrink-0 whitespace-nowrap', tab === 'account' && 'notch-tab-active']" @click="openTab('account')">{{ t('settings.tabs.account') }}</button>
          <button :class="['notch-tab shrink-0 whitespace-nowrap', tab === 'papers' && 'notch-tab-active']" @click="openTab('papers')">{{ t('settings.tabs.papers') }}</button>
          <button :class="['notch-tab shrink-0 whitespace-nowrap', tab === 'referral' && 'notch-tab-active']" @click="openTab('referral')">{{ t('settings.tabs.referral') }}</button>
          <button :class="['notch-tab shrink-0 whitespace-nowrap', tab === 'purchases' && 'notch-tab-active']" @click="openTab('purchases')">{{ t('settings.tabs.purchases') }}</button>
          <button :class="['notch-tab shrink-0 whitespace-nowrap', tab === 'invoices' && 'notch-tab-active']" @click="openTab('invoices')">{{ t('settings.tabs.invoices') }}</button>
          <button :class="['notch-tab shrink-0 whitespace-nowrap', tab === 'transactions' && 'notch-tab-active']" @click="openTab('transactions')">{{ t('settings.tabs.transactions') }}</button>
          <button v-if="props.hero" :class="['notch-tab shrink-0 whitespace-nowrap', tab === 'danger' && 'notch-tab-active']" @click="openTab('danger')">{{ t('settings.tabs.danger') }}</button>
        </nav>

        <!-- ══ ACCOUNT ══ -->
        <template v-if="tab === 'account'">
          <div class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.profile.title') }}</h2>
            <div class="st-row">
              <div><div class="st-label">{{ t('settings.profile.firstName.label') }}</div></div>
              <input v-model="profileFirstName" class="st-input" :placeholder="t('settings.profile.firstName.label')" />
            </div>
            <div class="st-row">
              <div><div class="st-label">{{ t('settings.profile.lastName.label') }}</div></div>
              <input v-model="profileLastName" class="st-input" :placeholder="t('settings.profile.lastName.label')" />
            </div>
            <div class="st-row">
              <div><div class="st-label">{{ t('settings.profile.phone.label') }}</div><div class="st-desc">{{ t('settings.profile.phone.desc') }}</div></div>
              <input v-model="profilePhone" class="st-input" :placeholder="t('settings.profile.phone.label')" />
            </div>
            <div class="flex gap-2 mt-3">
              <button
                class="btn-forge btn-forge--rune btn-forge--sm"
                :disabled="profileSaving || !profileDirty"
                @click="onSaveProfile"
              >{{ profileSaving ? t('common.saving') : t('settings.profile.saveDetails') }}</button>
            </div>
            <div class="st-row">
              <div><div class="st-label">{{ t('settings.profile.country.label') }}</div><div class="st-desc">{{ t('settings.profile.country.desc') }}</div></div>
              <div class="flex items-center gap-2">
                <select v-model="userCountry" class="st-input" style="min-width: 170px">
                  <option value="" disabled>{{ t('common.selectCountry') }}</option>
                  <option v-for="[code, name] in COUNTRIES" :key="code" :value="code">{{ name }}</option>
                </select>
                <button
                  class="btn-forge btn-forge--rune btn-forge--sm"
                  :disabled="countrySaving || userCountry.length !== 2 || userCountry === (account.country ?? '')"
                  @click="onSaveCountry"
                >{{ countrySaving ? t('common.working') : t('common.save') }}</button>
              </div>
            </div>
          </div>

          <!-- ══ TONGUE & COIN — the locale + currency switcher. ══════════════
               Both rows vanish on a single-language / single-currency build
               (see i18n/locales.ts): a control offering one option is a lie.
               If neither survives, so does the section heading. No Save button —
               the change lands on pick, which is the whole point of a switcher.
               Native <select>s on purpose: they inherit the .st-input chisel
               slot, and the OS picker is the one control that already works on
               a phone, with a keyboard, and in a screen reader. -->
          <div v-if="hasLocaleChoice || offeredCurrencies.length > 1" class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.prefs.title') }}</h2>

            <div v-if="hasLocaleChoice" class="st-row">
              <div>
                <div class="st-label">{{ t('settings.prefs.locale.label') }}</div>
                <div class="st-desc">{{ t('settings.prefs.locale.desc') }}</div>
              </div>
              <!-- Each option is written in its OWN language: someone stranded in
                   a tongue they can't read still recognises "Deutsch". -->
              <select
                class="st-input" style="min-width: 170px"
                :value="pickedLocale"
                @change="onPickLocale(($event.target as HTMLSelectElement).value as LocaleId)"
              >
                <option v-for="l in LOCALES" :key="l.id" :value="l.id" :lang="l.tag">{{ l.endonym }}</option>
              </select>
            </div>

            <div v-if="offeredCurrencies.length > 1" class="st-row">
              <div>
                <div class="st-label">{{ t('settings.prefs.currency.label') }}</div>
                <div class="st-desc">{{ t('settings.prefs.currency.desc') }}</div>
                <!-- The honest disclosure: a converted price is an estimate, and
                     the card is debited in the charge currency regardless. -->
                <div v-if="isConverted()" class="st-desc mt-1 not-italic text-gold/80">
                  {{ t('money.chargeNote', { currency: CHARGE_CURRENCY }) }}
                </div>
              </div>
              <select
                class="st-input" style="min-width: 170px"
                :value="pickedCurrency"
                @change="onPickCurrency(($event.target as HTMLSelectElement).value as CurrencyCode)"
              >
                <option v-for="c in offeredCurrencies" :key="c.code" :value="c.code">
                  {{ c.symbol }} {{ c.code }}
                </option>
              </select>
            </div>
          </div>

          <!-- ══ SOUND — the ambient bed and the combat effects. ══ -->
          <div class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.sound.title') }}</h2>
            <div class="st-row">
              <div>
                <div class="st-label">{{ t('settings.sound.music.label') }}</div>
                <div class="st-desc">{{ t('settings.sound.music.desc') }}</div>
              </div>
              <button class="btn-forge btn-forge--sm" :class="musicOn ? 'btn-forge--rune' : 'btn-forge--carved'" @click="onToggleMusic">
                {{ musicOn ? t('settings.sound.on') : t('settings.sound.off') }}
              </button>
            </div>
            <div class="st-row">
              <div>
                <div class="st-label">{{ t('settings.sound.effects.label') }}</div>
                <div class="st-desc">{{ t('settings.sound.effects.desc') }}</div>
              </div>
              <button class="btn-forge btn-forge--sm" :class="sfxOn ? 'btn-forge--rune' : 'btn-forge--carved'" @click="onToggleSfx">
                {{ sfxOn ? t('settings.sound.on') : t('settings.sound.off') }}
              </button>
            </div>
          </div>

          <div class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.password.title') }}</h2>
            <div class="flex flex-col gap-2 max-w-sm">
              <input v-model="pwCurrent" type="password" class="st-input w-full" :placeholder="t('settings.password.current')" autocomplete="current-password" />
              <input v-model="pwNew" type="password" class="st-input w-full" :placeholder="t('settings.password.new')" autocomplete="new-password" />
              <input
                v-model="pwConfirm" type="password" class="st-input w-full" :placeholder="t('settings.password.confirm')"
                autocomplete="new-password" @keydown.enter="onSavePassword"
              />
            </div>
            <div class="flex gap-2 mt-3">
              <button class="btn-forge btn-forge--rune btn-forge--sm" :disabled="pwSaving || !pwCurrent || !pwNew" @click="onSavePassword">
                {{ pwSaving ? t('common.saving') : t('settings.password.submit') }}
              </button>
              <button class="btn-forge btn-forge--carved btn-forge--sm" @click="pwCurrent = ''; pwNew = ''; pwConfirm = ''">{{ t('common.clear') }}</button>
            </div>
          </div>

          <!-- A way INTO the danger zone from the first tab. Retiring/deleting the
               hero lives on the last of seven tabs, which on a phone sits off the
               right edge of the scrolling tab strip — QA concluded there was no way
               to delete a character at all (2026-08-17). -->
          <div v-if="props.hero" class="ob-sec">
            <div class="st-row">
              <div>
                <div class="st-label">{{ t('settings.tabs.danger') }}</div>
                <div class="st-desc">{{ t('settings.danger.intro') }}</div>
              </div>
              <button class="btn-forge btn-forge--carved btn-forge--sm shrink-0" @click="openTab('danger')">
                {{ t('settings.danger.deleteConfirm') }}
              </button>
            </div>
          </div>
        </template>

        <!-- ══ PAPERS (KYC) ══ -->
        <template v-else-if="tab === 'papers'">
          <div class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.papers.title') }}</h2>

            <p v-if="!account.kycEnabled" class="font-body text-sm text-text-dim italic">
              {{ t('settings.papers.disabled') }}
            </p>

            <template v-else>
              <div v-if="account.kycStatus === 'APPROVED'" class="st-plate st-plate--green">
                {{ t('settings.papers.approved') }}
              </div>

              <div v-else-if="account.kycStatus === 'PENDING'" class="st-plate st-plate--amber">
                <span>
                  {{ t('settings.papers.pending') }}
                  <template v-if="account.kycSubmittedAt"> {{ t('settings.papers.submittedOn', { date: dateOf(account.kycSubmittedAt) }) }}</template>
                </span>
              </div>

              <template v-else>
                <!-- Two keys rather than one with an optional tail: gluing
                     " — {reason}" on in the template forces every translator to
                     reproduce an English dash convention they cannot see. -->
                <div v-if="account.kycStatus === 'REJECTED'" class="st-plate st-plate--red">
                  <span>{{ account.kycRejectReason
                    ? t('settings.papers.rejectedWithReason', { reason: account.kycRejectReason })
                    : t('settings.papers.rejected') }}</span>
                </div>
                <p class="font-body text-sm text-text-dim mb-4">
                  {{ t('settings.papers.intro') }}
                </p>
                <div class="flex flex-col gap-3 max-w-md">
                  <div><div class="st-label mb-1">{{ t('settings.papers.fullName') }}</div>
                    <input v-model="kycName" class="st-input w-full" :placeholder="t('settings.papers.fullNameHint')" /></div>
                  <div><div class="st-label mb-1">{{ t('settings.papers.dob') }}</div>
                    <input v-model="kycDob" type="date" class="st-input w-full" /></div>
                  <div><div class="st-label mb-1">{{ t('settings.papers.address') }}</div>
                    <input v-model="kycAddress" class="st-input w-full" :placeholder="t('settings.papers.addressHint')" /></div>
                  <div><div class="st-label mb-1">{{ t('settings.papers.country') }}</div>
                    <select v-model="kycCountry" class="st-input w-full">
                      <option value="" disabled>{{ t('common.selectCountry') }}</option>
                      <option v-for="[code, name] in COUNTRIES" :key="code" :value="code">{{ name }}</option>
                    </select></div>
                  <div><div class="st-label mb-1">{{ t('settings.papers.document') }}</div>
                    <input type="file" accept="image/*,.pdf" class="st-input st-file w-full" @change="onKycFile" /></div>
                  <button
                    class="btn-forge btn-forge--rune self-start"
                    :disabled="kycSubmitting || !kycFormValid"
                    @click="onSubmitKyc"
                  >{{ kycSubmitting ? t('settings.papers.submitting') : t('settings.papers.submit') }}</button>
                </div>
              </template>
            </template>
          </div>

          <!-- ══ THE LAW SHELF — the realm's published legal documents, straight
               from the admin CMS (the same set the web footer links). Hidden
               entirely when the directory is empty or unreachable. ══ -->
          <div v-if="legalDocs.length" class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.papers.legal.title') }}</h2>
            <p class="font-body text-sm text-text-dim mb-3">{{ t('settings.papers.legal.intro') }}</p>
            <div v-for="doc in legalDocs" :key="doc.slug" class="st-row">
              <!-- Founding papers translate via the footer's own keys; anything
                   admins add later shows its CMS title as written. -->
              <div><div class="st-label">{{ legalDocTitle(doc) }}</div></div>
              <button class="btn-forge btn-forge--carved btn-forge--sm shrink-0" @click="legalDocOpen = doc.slug">
                {{ t('settings.papers.legal.view') }}
              </button>
            </div>
          </div>
        </template>

        <!-- ══ REFERRAL ══ -->
        <template v-else-if="tab === 'referral'">
          <div class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.referral.title') }}</h2>

            <p v-if="referralError" class="font-body text-sm text-error">{{ referralError }}</p>
            <p v-else-if="!referralLoaded" class="font-body text-sm text-text-dim italic">{{ t('common.loading') }}</p>

            <template v-else-if="referral">
              <p class="font-body text-sm text-text-dim mb-4">
                {{ t('settings.referral.intro', { reward: formatNumber(referral.reward) }) }}
              </p>
              <div class="st-row">
                <div><div class="st-label">{{ t('settings.referral.link.label') }}</div><div class="st-desc">{{ t('settings.referral.link.desc') }}</div></div>
                <div class="flex items-center gap-2">
                  <input
                    class="st-input" :value="referralLink" readonly style="min-width: 240px"
                    @focus="($event.target as HTMLInputElement).select()"
                  />
                  <button class="btn-forge btn-forge--rune btn-forge--sm" @click="copyReferralLink">
                    {{ referralCopied ? t('common.copied') : t('common.copy') }}
                  </button>
                </div>
              </div>
            </template>
          </div>
        </template>

        <!-- ══ PURCHASES ══ -->
        <template v-else-if="tab === 'purchases'">
          <div class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.purchases.title') }}</h2>
            <p v-if="purchasesLoaded && !purchases.length" class="font-body text-sm text-text-dim italic">{{ t('settings.purchases.empty') }}</p>
            <div v-else class="st-table">
              <div v-for="h in purchases" :key="h.id" class="st-tr">
                <span class="text-text-dim">{{ dateOf(h.createdAt) }}</span>
                <span class="text-text truncate">{{ t('settings.purchases.line', { pack: h.pack, count: formatNumber(h.bars) }) }}</span>
                <span class="text-right text-text">{{ price(h.amountCents, h.currency) }}</span>
                <span class="text-right" :class="h.status === 'COMPLETED' ? 'text-success' : 'text-text-dim'">{{ purchaseStatus(h.status) }}</span>
              </div>
            </div>
          </div>

          <button class="btn-forge btn-forge--rune btn-forge--sm" @click="emit('goto-reserve')">
            {{ t('settings.purchases.openHoard') }}
          </button>
        </template>

        <!-- ══ INVOICES ══ -->
        <template v-else-if="tab === 'invoices'">
          <div class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.invoices.title') }}</h2>
            <p v-if="invoicesLoaded && !invoices.length" class="font-body text-sm text-text-dim italic">{{ t('settings.invoices.empty') }}</p>
            <div v-else class="st-table">
              <div v-for="inv in invoices" :key="inv.invoiceNumber" class="st-tr">
                <span class="text-text-dim">{{ dateOf(inv.date) }}</span>
                <span class="truncate">
                  <span class="text-text">{{ inv.invoiceNumber }}</span>
                  <span :class="inv.status === 'PAID' ? 'text-success' : 'text-text-dim'"> · {{ invoiceStatus(inv.status) }}</span>
                </span>
                <span class="text-right text-text">{{ price(inv.totalCents, inv.currency) }}</span>
                <button
                  class="text-right text-gold hover:text-gold-bright disabled:opacity-50"
                  :disabled="invoiceBusy === inv.invoiceNumber"
                  @click="onDownloadInvoice(inv.invoiceNumber)"
                >{{ invoiceBusy === inv.invoiceNumber ? t('common.working') : t('common.pdf') }}</button>
              </div>
            </div>
          </div>
        </template>

        <!-- ══ TRANSACTIONS (full cash + gold ledger) ══ -->
        <template v-else-if="tab === 'transactions'">
          <div class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.transactions.title') }}</h2>
            <p v-if="ledgerLoaded && !ledger.length" class="font-body text-sm text-text-dim italic">
              {{ t('settings.transactions.empty') }}
            </p>
            <div v-else class="st-table">
              <div class="st-tr st-tr--head">
                <span>{{ t('settings.transactions.when') }}</span><span>{{ t('settings.transactions.detail') }}</span><span class="text-right">{{ t('settings.transactions.amount') }}</span><span class="text-right">{{ t('settings.transactions.balance') }}</span>
              </div>
              <!-- `row`, not `t` — the loop variable would otherwise shadow the
                   translate function for the whole block. -->
              <div v-for="row in ledger" :key="row.id" class="st-tr">
                <span class="text-text-dim">{{ timeOf(row.createdAt) }}</span>
                <span class="text-text truncate flex items-center gap-1.5">
                  <img :src="row.currency === 'PREMIUM' ? runesIcon : cashIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
                  {{ row.description }}
                </span>
                <span class="text-right font-heading tabular-nums" :class="row.amount >= 0 ? 'text-success' : 'text-error'">
                  {{ row.amount >= 0 ? '+' : '' }}{{ formatNumber(row.amount) }}
                </span>
                <span class="text-right text-text-dim tabular-nums">{{ formatNumber(row.balanceAfter) }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- ══ DANGER ZONE — delete the character or reroll into a new class ══ -->
        <template v-else-if="tab === 'danger' && props.hero">
          <div class="ob-sec">
            <h2 class="gilt-text">{{ t('settings.danger.title') }}</h2>
            <p class="font-body text-sm text-text-dim italic mb-4">{{ t('settings.danger.intro') }}</p>

            <p v-if="!rerollQuoteLoaded" class="font-body text-sm text-text-dim italic">{{ t('common.loading') }}</p>

            <template v-else>
              <div class="st-row">
                <div>
                  <div class="st-label">{{ t('settings.danger.inventoryValue') }}</div>
                  <div class="st-desc">{{ t('settings.danger.inventoryValueDesc') }}</div>
                </div>
                <span class="font-heading tabular-nums text-text flex items-center gap-1.5 flex-wrap justify-end">
                  <span class="flex items-center gap-1.5">
                    <img :src="cashIcon" class="w-3.5 h-3.5" alt="" />{{ formatNumber(rerollQuote?.inventoryValue ?? 0) }}
                  </span>
                  <!-- Rune-priced gear is a separate unit — shown beside the silver, never added to it -->
                  <span v-if="(rerollQuote?.inventoryValuePremium ?? 0) > 0" class="flex items-center gap-1.5">
                    <img :src="runesIcon" class="w-3.5 h-3.5" alt="" />{{ formatNumber(rerollQuote?.inventoryValuePremium ?? 0) }}
                  </span>
                </span>
              </div>
              <div class="st-row">
                <div>
                  <div class="st-label">{{ t('settings.danger.compensation') }}</div>
                  <div class="st-desc">{{ t('settings.danger.compensationDesc') }}</div>
                </div>
                <span class="font-heading tabular-nums text-success flex items-center gap-1.5 flex-wrap justify-end">
                  <span class="flex items-center gap-1.5">
                    <img :src="cashIcon" class="w-3.5 h-3.5" alt="" />{{ formatNumber(rerollQuote?.compensation ?? 0) }}
                  </span>
                  <span v-if="(rerollQuote?.compensationPremium ?? 0) > 0" class="flex items-center gap-1.5">
                    <img :src="runesIcon" class="w-3.5 h-3.5" alt="" />{{ formatNumber(rerollQuote?.compensationPremium ?? 0) }}
                  </span>
                </span>
              </div>

              <!-- Change class ── -->
              <div class="mt-5">
                <div class="font-heading font-bold text-text mb-1">{{ t('settings.danger.rerollSectionTitle') }}</div>
                <p class="font-body text-sm text-text-dim mb-3">{{ t('settings.danger.rerollSectionDesc') }}</p>
                <div class="flex flex-col gap-3 max-w-md">
                  <div>
                    <div class="st-label mb-1">{{ t('settings.danger.pickClass') }}</div>
                    <select v-model="rerollClass" class="st-input w-full">
                      <option v-for="c in HERO_CLASSES" :key="c" :value="c">{{ classLabel(c) }}</option>
                    </select>
                  </div>
                  <div>
                    <div class="st-label mb-1">{{ t('settings.danger.newName') }}</div>
                    <input
                      v-model="rerollName" class="st-input w-full" maxlength="50"
                      :placeholder="props.hero?.hero.name ?? ''"
                    />
                  </div>
                  <button
                    class="btn-forge btn-forge--blood self-start"
                    :disabled="dangerBusy || !rerollQuoteLoaded"
                    @click="onRerollHero"
                  >{{ t('settings.danger.rerollConfirm') }}</button>
                </div>
              </div>

              <!-- Delete character ── -->
              <div class="mt-6">
                <div class="st-plate st-plate--red mb-3">{{ t('settings.danger.deleteWarning') }}</div>
                <button
                  class="btn-forge btn-forge--blood self-start"
                  :disabled="dangerBusy || !rerollQuoteLoaded"
                  @click="onDeleteHero"
                >{{ t('settings.danger.deleteConfirm') }}</button>
              </div>
            </template>
          </div>
        </template>

      </div>
    </div>

    <!-- The reader — a fixed overlay, so it sits outside the book spread. -->
    <LegalPanel
      v-if="legalDocOpen"
      :docs="legalDocs"
      :initial-slug="legalDocOpen"
      @close="legalDocOpen = null"
    />
  </section>
</template>

<style scoped>
/* ── the page title, struck once above the book (venue grammar: gilt display
   + the rune divider — same as every hall's front). ── */
.ob-h1 { margin-bottom: 0; }
.ob-h1::after {
  content: '';
  display: block;
  width: 220px; height: 9px;
  margin: 8px 0 18px;
  background: var(--orn-divider-gold) no-repeat left center / contain;
}

/* ── THE SPREAD — one open book. Two pages on one plank, a stitched spine
   between them. The oath page is fixed; the ledger page turns. ── */
.ob-spread {
  display: grid;
  grid-template-columns: 252px 30px minmax(0, 1fr);
  align-items: stretch;
}

/* ── the oath page ── */
.ob-oath {
  display: flex; flex-direction: column; align-items: center; text-align: center;
  gap: 7px;
  padding: 26px 18px 20px;
}
/* the portrait plaque — the page's one warm focal point ("this is you"):
   btn-forge anatomy (an ember rim showing around a hewn-cut plate), not a
   web-app avatar circle. */
.ob-portrait {
  width: 128px; height: 128px;
  padding: 3px;
  clip-path: var(--clip-hewn);
  background: linear-gradient(180deg,
    color-mix(in srgb, var(--color-ember) 75%, #fff) 0%,
    color-mix(in srgb, var(--color-ember) 55%, transparent) 30%,
    rgba(20, 12, 6, 0.9) 100%);
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.6));
}
.ob-portrait img,
.ob-portrait-rune {
  width: 100%; height: 100%;
  clip-path: var(--clip-hewn);
  object-fit: cover; object-position: top;
}
.ob-portrait-rune {
  display: grid; place-items: center;
  background: var(--sunken);
  font-size: 44px; color: var(--color-gold);
}
.ob-id { display: flex; flex-direction: column; align-items: center; gap: 6px; min-width: 0; }
.ob-name {
  font-family: var(--font-heading, serif); font-weight: 900;
  font-size: 19px; letter-spacing: 0.04em; line-height: 1.15;
  color: #f0e6d0;
  overflow-wrap: anywhere;
}
/* standing marks — the compact status plates under the portrait */
.ob-marks {
  width: 100%;
  display: flex; flex-direction: column; gap: 8px;
  /* the bottom margin is the guaranteed gap to the admin link below when the
     page runs tight (its auto margin collapses to zero under pressure) */
  margin: 12px 0 18px;
  text-align: left;
}
.ob-marks .st-plate { margin-bottom: 0; font-size: 12.5px; padding: 8px 10px 8px 17px; }
/* pinned to the page's foot (the ledger page is usually the taller one) */
.ob-admin { margin-top: auto; align-self: stretch; }

/* ── the spine — the stitched binding between the pages ── */
.ob-spine {
  position: relative;
  background: linear-gradient(90deg,
    transparent 0%, rgba(0, 0, 0, 0.4) 30%,
    rgba(0, 0, 0, 0.72) 50%,
    rgba(0, 0, 0, 0.4) 70%, transparent 100%);
}
/* the binding cord, laced through in intervals */
.ob-spine::before {
  content: '';
  position: absolute; left: 50%; top: 16px; bottom: 16px; width: 2px;
  transform: translateX(-50%);
  background: repeating-linear-gradient(180deg,
    color-mix(in srgb, var(--color-gold) 55%, transparent) 0 12px,
    transparent 12px 30px);
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.9));
}

/* ── the ledger page ── */
.ob-ledger { min-width: 0; padding: 18px 22px 22px; }
.ob-sec { padding-bottom: 6px; }
.ob-sec + .ob-sec { margin-top: 14px; }
/* the section heading grammar — same rune divider as every hall's panels */
.ob-sec > h2 {
  font-family: var(--font-heading, serif);
  font-size: 19px; letter-spacing: 0.02em; text-transform: none;
  margin-bottom: 6px;
}
.ob-sec > h2::after {
  content: '';
  display: block;
  width: 200px; height: 9px;
  margin-top: 7px; margin-bottom: 16px;
  background: var(--orn-divider-gold) no-repeat left center / contain;
}

/* ── status plates — carved slots, not tinted boxes. A struck rune tick in
   the leading edge carries the colour; the material stays the same cold
   sunken stone in every state. ── */
.st-plate {
  position: relative;
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px 10px 20px; margin-bottom: 12px;
  font-family: var(--font-body, serif); font-size: 14px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  color: var(--color-text);
}
.st-plate::before {
  content: '';
  position: absolute; left: 7px; top: 50%; width: 3px; height: 58%;
  transform: translateY(-50%);
  background: var(--pl-tone, var(--color-text-dim));
  filter: drop-shadow(0 0 6px color-mix(in srgb, var(--pl-tone, transparent) 70%, transparent));
}
.st-plate--green { --pl-tone: #4ade80; color: #4ade80; }
.st-plate--amber { --pl-tone: #d69e2e; color: #d69e2e; justify-content: space-between; }
.st-plate--red   { --pl-tone: #f87171; color: #f87171; }
.st-plate--dim   { color: var(--color-text-dim); }

.st-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase;
  color: var(--color-text-dim);
}
.st-badge--gold { box-shadow: var(--rim-ice-sunken), inset 0 0 0 1px color-mix(in srgb, var(--color-gold-bright) 40%, transparent); color: var(--color-gold-bright); }

/* ── rows + inputs — every field is a chisel slot cut into the plank, never
   a bordered rectangle. ── */
.st-row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 12px 0;
  position: relative;
}
.st-row:not(:last-child)::after {
  content: '';
  position: absolute; left: 0; right: 0; bottom: 0; height: 2px;
  background:
    linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)) top / 100% 1px no-repeat,
    linear-gradient(rgba(207, 232, 255, 0.12), rgba(207, 232, 255, 0.12)) bottom / 100% 1px no-repeat;
}
.st-label {
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase;
  color: #bccbdb;
}
.st-desc { font-family: var(--font-body, serif); font-size: 12px; color: var(--color-text-dim); font-style: italic; }
.st-input {
  padding: 9px 12px;
  border: 0;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  font-family: var(--font-body, serif); font-size: 14px;
  color: var(--color-text);
  min-width: 160px;
}
.st-input:focus {
  outline: none;
  box-shadow:
    var(--rim-ice-sunken),
    inset 0 0 16px color-mix(in srgb, var(--color-aurora) 18%, transparent),
    inset 0 -1px 0 color-mix(in srgb, var(--color-aurora) 55%, transparent);
}
.st-input[readonly] { color: var(--color-text-dim); }
.st-file { padding: 7px 10px; font-size: 13px; }
select.st-input option { background: #0e1826; }

/* ── ledger lines — purchases / invoices / transactions all share ONE grid
   declaration, so date and amount always land on the same right axes no
   matter which list is open. Row separators are carved hairlines (a dark
   groove with a light lip just under it), never a 1px box border. ── */
.st-table { display: flex; flex-direction: column; }
.st-tr {
  position: relative;
  display: grid; grid-template-columns: auto 1fr auto auto;
  gap: 14px; align-items: center;
  padding: 10px 6px;
  font-family: var(--font-body, serif); font-size: 13px;
}
.st-tr:not(:last-child)::after {
  content: '';
  position: absolute; left: 6px; right: 6px; bottom: 0; height: 2px;
  background:
    linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)) top / 100% 1px no-repeat,
    linear-gradient(rgba(207, 232, 255, 0.12), rgba(207, 232, 255, 0.12)) bottom / 100% 1px no-repeat;
}
.st-tr--head {
  font-family: var(--font-label, sans-serif);
  font-size: 9px; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--color-text-dim);
}

/* On a phone the spread closes into a single page: the oath page becomes a
   horizontal band (portrait left, name and standing right), the spine turns
   into a horizontal stitch line, and the notch beam wraps below it. */
@media (max-width: 760px) {
  .ob-spread { grid-template-columns: 1fr; }
  .ob-oath {
    flex-direction: row; flex-wrap: wrap; align-items: center;
    text-align: left; column-gap: 16px;
    padding: 16px 14px 14px;
  }
  .ob-portrait { width: 84px; height: 84px; }
  .ob-portrait-rune { font-size: 30px; }
  /* identity flows as one column beside the portrait */
  .ob-id { flex: 1 1 0; align-items: flex-start; text-align: left; }
  .ob-badges { justify-content: flex-start; }
  .ob-marks { margin-top: 10px; }
  .ob-admin { align-self: stretch; width: 100%; }
  /* label-left/control-right rows can't hold a line at phone width — let the
     control wrap under its label instead of squeezing past the page edge */
  .st-row { flex-wrap: wrap; row-gap: 8px; }
  .st-input { max-width: 100%; }
  .ob-spine {
    height: 24px;
    background: linear-gradient(180deg,
      transparent 0%, rgba(0, 0, 0, 0.4) 30%,
      rgba(0, 0, 0, 0.72) 50%,
      rgba(0, 0, 0, 0.4) 70%, transparent 100%);
  }
  .ob-spine::before {
    left: 16px; right: 16px; top: 50%; bottom: auto; width: auto; height: 2px;
    transform: translateY(-50%);
    background: repeating-linear-gradient(90deg,
      color-mix(in srgb, var(--color-gold) 55%, transparent) 0 12px,
      transparent 12px 30px);
  }
  .ob-ledger { padding: 14px 12px 16px; }
}

/* Billing tables: fixed-column grids can't fit a phone width, so each row
   stacks — the name/detail on its own line, the rest inline under it. Keeps
   the page to vertical scroll only. */
@media (max-width: 560px) {
  .st-tr {
    display: flex; flex-wrap: wrap; align-items: baseline;
    grid-template-columns: none; gap: 2px 12px; padding: 10px 4px;
  }
  .st-tr > * { text-align: left !important; font-size: 12px; }
  .st-tr > :nth-child(2) { order: -1; flex-basis: 100%; font-size: 14px; }
  /* column-header row is meaningless once rows stack — hide it (needs to beat
     the .st-tr flex rule above, so match with two classes) */
  .st-tr.st-tr--head { display: none; }
}
</style>
